/**
 * Ojar Free Samples Manager
 * - Limits samples to MAX 2
 * - Shows "FREE" for all sample products
 * - Excludes samples from checkout totals
 * - Works with 'sample-product' tagged items
 * - Only shows for qualifying SKUs (EDP 100ml, EXT 100ml, ABS 20ml)
 * - Auto-removes samples when qualifying product is removed from cart
 */

(function () {
  'use strict';

  const MAX_FREE_SAMPLES = 2;
  const freeLabel = window.ojarStrings?.labels?.free || '';
  const freeSampleLimitTemplate =
    window.ojarStrings?.freeSamples?.limitAlert ||
    '{{ count }}';
  let isProcessing = false;
  let observerDebounceTimeout = null;

  // ✅ Qualifying SKU patterns for free sample eligibility
  const QUALIFYING_SKU_PATTERNS = [
    /^OJ-EDP-.*-100$/,
    /^OJ-EXT-.*-100$/,
    /^OJ-ABS-.*-020$/
  ];

  function init() {
    console.log('[FreeSamples] Initializing...');
    enforceSampleLimit();
    displayFreePrices();
    interceptSampleForms();
    observeCartChanges();
    overrideFetch();
    console.log('[FreeSamples] Initialized successfully');
  }

  function displayFreePrices() {
    document.querySelectorAll('.oj-complimentary-samples__wrapper .card-wrapper .price').forEach(el => {
      if (!el.closest('.ojar-free-hidden') && !el.querySelector('.ojar-free-price') && !el.closest('s')) {
        el.innerHTML = '<span class="ojar-free-price" style="color: #2e7d32; font-weight: bold;">' + freeLabel + '</span>';
      }
    });
  }

  function replacePricesWithFree(item) {
    const finalPriceSelectors = [
      '.cart-item__totals .price--end:not(s .price--end)',
      '.cart-item__totals > .price:not(s .price)',
      '.oj-line-item__price',
      '.cart-item__final-price'
    ];

    if (item.querySelector('.oj-sample-free-tag') || item.querySelector('.ojar-free-price')) {
      return;
    }

    let replaced = false;
    finalPriceSelectors.forEach(selector => {
      item.querySelectorAll(selector).forEach(el => {
        if (!el.closest('s') && !el.querySelector('.ojar-free-price') && !el.classList.contains('oj-sample-free-tag') && !replaced) {
          el.innerHTML = '<span class="ojar-free-price" style="color: #2e7d32; font-weight: bold;">' + freeLabel + '</span>';
          replaced = true;
        }
      });
    });

    if (!replaced) {
      item.querySelectorAll('.price--end, .price-item--sale').forEach(el => {
        if (!el.closest('s') && !el.classList.contains('oj-sample-free-tag') && !replaced) {
          el.innerHTML = '<span class="ojar-free-price" style="color: #2e7d32; font-weight: bold;">' + freeLabel + '</span>';
          replaced = true;
        }
      });
    }

    item.querySelectorAll('s').forEach(s => {
      if (s.textContent.includes(freeLabel)) {
        s.style.display = 'none';
      }
    });
  }

  // ✅ SKU qualification check
  function isQualifyingSKU(sku) {
    if (!sku) return false;
    return QUALIFYING_SKU_PATTERNS.some(pattern => pattern.test(sku));
  }

  function hasQualifyingProduct(cartItems) {
    return cartItems.some(item => isQualifyingSKU(item.sku));
  }

  async function enforceSampleLimit() {
    if (isProcessing) return;
    isProcessing = true;

    try {
      const cart = await fetchCart();

      // ✅ Only show samples if a qualifying product is in the cart
      if (!hasQualifyingProduct(cart.items)) {
        hideSampleSelection();
        disableSampleButtons();

        // ✅ Remove any samples already in the cart
        const samplesToRemove = [];
        for (const item of cart.items) {
          try {
            const product = await fetchProduct(item.handle);
            if (product.tags && product.tags.includes('sample-product')) {
              samplesToRemove.push(item.key);
            }
          } catch (e) {
            console.warn('[FreeSamples] Could not verify product:', item.handle);
          }
        }

        if (samplesToRemove.length > 0) {
          for (const key of samplesToRemove) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 })
            });
          }
          await refreshCartDrawer();
        }

        isProcessing = false;
        return;
      }

      let sampleCount = 0;
      const sampleItems = [];

      for (const item of cart.items) {
        const cartItemEl = document.querySelector(`[data-key="${item.key}"]`);
        let isSample = cartItemEl?.dataset?.tags?.includes('sample-product') ||
          cartItemEl?.dataset?.isSample === 'true' ||
          cartItemEl?.classList?.contains('cart-item__sample');

        if (!isSample) {
          try {
            const product = await fetchProduct(item.handle);
            isSample = product.tags && product.tags.includes('sample-product');
          } catch (e) {
            console.warn('[FreeSamples] Could not verify product:', item.handle);
          }
        }

        if (isSample) {
          sampleCount += item.quantity;
          sampleItems.push({ key: item.key, quantity: item.quantity, handle: item.handle });
        }
      }

      console.log('[FreeSamples] Sample count:', sampleCount, '/', MAX_FREE_SAMPLES);
      updateCounters(sampleCount);

      if (sampleCount > MAX_FREE_SAMPLES) {
        const excess = sampleCount - MAX_FREE_SAMPLES;
        await removeExcessSamples(sampleItems, excess);
        return;
      }

      if (sampleCount >= MAX_FREE_SAMPLES) {
        hideSampleSelection();
        disableSampleButtons();
      } else {
        showSampleSelection();
        enableSampleButtons();
      }

      displayFreePrices();

    } catch (error) {
      console.error('[FreeSamples] Error:', error);
    } finally {
      isProcessing = false;
    }
  }

  function updateCounters(currentCount) {
    document.querySelectorAll('.oj-sample__current-count').forEach(el => { el.textContent = currentCount; });
    document.querySelectorAll('.oj-sample__total-count').forEach(el => { el.textContent = MAX_FREE_SAMPLES; });
  }

  function hideSampleSelection() {
    document.querySelectorAll('.oj-complimentary-samples__container').forEach(el => { el.style.display = 'none'; });
  }

  function showSampleSelection() {
    document.querySelectorAll('.oj-complimentary-samples__container').forEach(el => { el.style.display = ''; });
  }

  function disableSampleButtons() {
    document.querySelectorAll('.oj-complimentary-samples__wrapper button, .oj-complimentary-samples__wrapper .quick-add__submit').forEach(btn => {
      btn.disabled = true; btn.style.pointerEvents = 'none'; btn.style.opacity = '0.5';
    });
    document.querySelectorAll('.cart-item__sample .quantity__button[name="plus"]').forEach(btn => {
      btn.disabled = true; btn.style.pointerEvents = 'none'; btn.style.opacity = '0.5';
    });
  }

  function enableSampleButtons() {
    document.querySelectorAll('.oj-complimentary-samples__wrapper button, .oj-complimentary-samples__wrapper .quick-add__submit').forEach(btn => {
      btn.disabled = false; btn.style.pointerEvents = ''; btn.style.opacity = '';
    });
    document.querySelectorAll('.cart-item__sample .quantity__button[name="plus"]').forEach(btn => {
      btn.disabled = false; btn.style.pointerEvents = ''; btn.style.opacity = '';
    });
  }

  async function removeExcessSamples(sampleItems, excess) {
    let remaining = excess;
    for (let i = sampleItems.length - 1; i >= 0 && remaining > 0; i--) {
      const item = sampleItems[i];
      const toRemove = Math.min(remaining, item.quantity);
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: item.quantity - toRemove })
      });
      remaining -= toRemove;
    }
    await refreshCartDrawer();
    setTimeout(() => { enforceSampleLimit(); displayFreePrices(); }, 300);
  }

  function interceptSampleForms() {
    document.querySelectorAll('.oj-complimentary-samples__wrapper form').forEach(form => {
      if (form.dataset.freeSampleIntercepted) return;
      form.dataset.freeSampleIntercepted = 'true';

      form.addEventListener('submit', async function (e) {
        e.preventDefault();
        e.stopPropagation();

        const cart = await fetchCart();
        let count = await countSamplesInCart(cart);

        if (count >= MAX_FREE_SAMPLES) {
          alert(freeSampleLimitTemplate.replace('{{ count }}', String(MAX_FREE_SAMPLES)));
          return false;
        }

        const formData = new FormData(form);
        await fetch('/cart/add.js', { method: 'POST', body: formData });
        await refreshCartDrawer();
        setTimeout(() => { enforceSampleLimit(); displayFreePrices(); }, 300);
      }, true);
    });
  }

  async function countSamplesInCart(cart) {
    let count = 0;
    for (const item of cart.items) {
      try {
        const product = await fetchProduct(item.handle);
        if (product.tags && product.tags.includes('sample-product')) count += item.quantity;
      } catch (e) { }
    }
    return count;
  }

  function observeCartChanges() {
    const targets = [
      document.getElementById('CartDrawer'),
      document.getElementById('cart_page'),
      document.querySelector('cart-drawer'),
      document.querySelector('cart-items')
    ].filter(Boolean);

    targets.forEach(target => {
      const observer = new MutationObserver(() => {
        if (observerDebounceTimeout) clearTimeout(observerDebounceTimeout);
        observerDebounceTimeout = setTimeout(() => {
          if (!isProcessing) {
            enforceSampleLimit();
            displayFreePrices();
            interceptSampleForms();
          }
        }, 0);
      });
      observer.observe(target, { childList: true, subtree: true });
    });
  }

  function overrideFetch() {
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const result = await originalFetch.apply(this, args);
      const url = args[0];
      if (typeof url === 'string') {
        if ((url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update')) && !url.includes('/cart/restore')) {
          setTimeout(enforceSampleLimit, 300);
          setTimeout(displayFreePrices, 400);
          setTimeout(interceptSampleForms, 500);
        }
      }
      return result;
    };
  }

  async function fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  async function refreshCartDrawer() {
    try {
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newDrawerItems = doc.querySelector('cart-drawer-items');
      const currentDrawerItems = document.querySelector('cart-drawer-items');
      if (newDrawerItems && currentDrawerItems) currentDrawerItems.innerHTML = newDrawerItems.innerHTML;

      const newFooter = doc.querySelector('.cart-drawer__footer');
      const currentFooter = document.querySelector('.cart-drawer__footer');
      if (newFooter && currentFooter) currentFooter.innerHTML = newFooter.innerHTML;

      const iconBubbleResponse = await fetch('/?section_id=cart-icon-bubble');
      const iconHtml = await iconBubbleResponse.text();
      const iconDoc = parser.parseFromString(iconHtml, 'text/html');
      const newBubble = iconDoc.querySelector('.cart-count-bubble');
      const currentBubble = document.querySelector('.cart-count-bubble');
      if (newBubble && currentBubble) currentBubble.innerHTML = newBubble.innerHTML;

      console.log('[FreeSamples] Cart drawer refreshed');
    } catch (error) {
      console.error('[FreeSamples] Error refreshing cart drawer:', error);
      window.location.reload();
    }
  }

  async function fetchProduct(handle) {
    const response = await fetch(`/products/${handle}.js`);
    return response.json();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.OjarFreeSamples = { enforceSampleLimit, displayFreePrices, MAX_FREE_SAMPLES };

})();
